import React, { useState, useEffect, useCallback } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Select, Dropdown, Button } from 'antd'
import Breadcrumbs from '../../components/Breadcrumbs'
import DrawerNav from '../../components/DrawerNav'
import { useSidebar, useSidebarSafe } from '../../contexts/SidebarContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import { useGetGeneralSettingsQuery } from '../../services/settingsSlice'
import { useGetCustomersEmailQuery } from '../../services/createProfile'
import { useDispatch } from 'react-redux'
import { updateNewConversation } from '../../services/staticSlice'
import { baseApi } from '../../services/baseQuery'
import { logOut } from '../../store/slice/userAuth'
import { getAuth, signOut as firebaseSignOut } from 'firebase/auth'
import { signOutCognitoUser } from '../../services/auth'
import { PlusIcon, UserIcon, IntegrationIcon, SettingsIcon, LogoutIcon, ChevronSelectorVertical, HelpIcon } from '../../components/Icons/iconImages'
import { useOnboardingTour } from '../../Hooks/useOnboardingTour'
import { settingsTour } from '../../components/OnboardingTour/tourConfigs'
import SafeOnboardingTour from '../../components/OnboardingTour/SafeOnboardingTour'
import TourDebugHelper from '../../components/OnboardingTour/TourDebugHelper'
import "./styles.css"

function CompanySettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sidebarContext = useSidebarSafe();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isInIframe] = useState(window.self !== window.top);
  const { getCurrentWorkspaceObjectId, currentWorkspace, workspaces, switchWorkspace, refreshWorkspaces } = useWorkspace();
  const { isMobileMenuOpen, isSidebarOpen, toggleSidebar, closeMobileMenu } = useSidebar();

  // Settings tour
  const {
    runTour,
    currentTour,
    closeTour,
    skipTour,
    startTour,
  } = useOnboardingTour(settingsTour, {
    delay: 1000,
    autoStart: false, // Don't auto-start, only start manually from tour helper
    chainToNext: true // Enable tour chaining (last tour in sequence)
  });

  // Listen for manual tour start events
  useEffect(() => {
    const handleStartTour = (event: any) => {
      const tourData = event.detail;
      // Only start this tour if it matches the settings tour and no tour is currently running
      if (tourData && tourData.id === 'settings-tour' && !runTour) {
        startTour(tourData, tourData.isChainedTour || false);
      }
    };

    window.addEventListener('startTour', handleStartTour);
    return () => {
      window.removeEventListener('startTour', handleStartTour);
    };
  }, [startTour, runTour]);

  const { data: userData } = useGetGeneralSettingsQuery('', {
    refetchOnMountOrArgChange: true,
  });
  const getUserData = userData?.settings;

  // Oogwai user check and impersonation
  const localEmail = localStorage.getItem("oogw.ai_user_email");
  const getEmail = (!localEmail || localEmail === "null") ? getUserData?.email : localEmail;
  const oogwaiUser = getEmail && getEmail !== "info@oogw.ai" && getEmail !== "demo@oogw.ai" ? getEmail?.split('@')[1]?.includes('oogw.ai') : false;

  const { data: customersEmail } = useGetCustomersEmailQuery("", {
    skip: !oogwaiUser
  });

  const [shadowUser, setShadowUser] = useState<string | null>(null);
  const storedShadowUser = localStorage.getItem('oogw.ai_shadow_user_email');

  useEffect(() => {
    setShadowUser(storedShadowUser && storedShadowUser !== 'undefined' ? storedShadowUser : null);
  }, [storedShadowUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLinkClick = () => {
    // Close mobile menu when a link is clicked
    if (sidebarContext && isMobile) {
      sidebarContext.closeMobileMenu();
    }
  };

  const handleLogout = useCallback(() => {
    dispatch(logOut());
    const auth = getAuth();
    firebaseSignOut(auth);
    localStorage.removeItem("oogw.ai_user");
    localStorage.removeItem("oogw.ai_user_type");
    localStorage.removeItem("workspace_id");
    localStorage.removeItem('oogw.ai_shadow_user_email');
    // Clear tour session flag so first-time tour can show on next login
    sessionStorage.removeItem('oogwai_first_time_tour_shown');
    navigate("/login");
    window.location.reload();
    dispatch(baseApi.util.resetApiState());
    signOutCognitoUser();
  }, [dispatch, navigate]);

  return (
    <div className='space-y-3 mt-10'>
      {/* Tour Helper for debugging/testing - disabled in iframe */}
      {!isInIframe && <TourDebugHelper />}

      {/* Onboarding Tour - disabled in iframe */}
      {!isInIframe && (
        <SafeOnboardingTour
          isOpen={runTour}
          tourData={currentTour}
          onComplete={closeTour}
          onSkip={skipTour}
        />
      )}

      {/* Mobile overlay with dim background */}
      {sidebarContext?.isMobileMenuOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/70 z-20 md:hidden transition-opacity duration-300"
          onClick={sidebarContext.closeMobileMenu}
        />
      )}

      {/* Breadcrumbs for desktop */}
      <div className='hidden md:grid grid-cols-12'>
        <div className='col-span-3 xl:col-span-2'></div>
        <div className='col-span-9 xl:col-span-10 pl-5'>
          <Breadcrumbs />
        </div>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6'>
        {/* Desktop Sidebar Navigation */}
        <div className='hidden md:block md:col-span-3 xl:col-span-2'>
          <div className='sticky top-4'>
            <DrawerNav onLinkClick={handleLinkClick} />
          </div>
        </div>

        {/* Mobile Sidebar Navigation - Slide-in */}
        <div
          className={`fixed top-0 left-0 h-screen bg-white z-30 transition-transform duration-300 ease-in-out md:hidden flex flex-col ${sidebarContext?.isMobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
            }`}
          style={{
            width: isMobile ? '100%' : (isSidebarOpen ? '250px' : '52px'),
            height: isMobile ? '100%' : 'calc(100vh)',
            left: '0',
            paddingBottom: isMobile ? 'calc(env(safe-area-inset-top) + 64px)' : '0',
            paddingTop: 'calc(env(safe-area-inset-top) + 64px)'
          }}
        // style={{ width: '100%', paddingTop: 'calc(env(safe-area-inset-top) + 64px)' }}
        >
          {/* Workspace Selector - Mobile Only */}
          <div className="px-3 py-3 flex-shrink-0">
            <p className="text-xs font-medium text-gray-500 mb-2">Workspace</p>
            <Select
              onChange={async (value) => {
                if (value === 'add-profile') {
                  navigate('/create-profile');
                  if (sidebarContext) sidebarContext.closeMobileMenu();
                } else if (value && value !== currentWorkspace) {
                  await switchWorkspace(value);
                  if (sidebarContext) sidebarContext.closeMobileMenu();
                }
              }}
              value={currentWorkspace || ""}
              className="w-full"
              dropdownStyle={{ width: 'auto' }}
              popupMatchSelectWidth={false}
              optionRender={(item) => {
                return (
                  <div className="flex items-center gap-2 py-1 justify-center cursor-pointer">
                    {item.value === 'add-profile' ? item.label : item.label}
                  </div>
                )
              }}
              dropdownRender={(menu) => (
                <div className="text-center">
                  {menu}
                </div>
              )}
            >
              {workspaces?.map((workspace) => (
                <Select.Option key={workspace._id} value={workspace._id}>
                  {workspace.workspace_id}
                </Select.Option>
              ))}
              <Select.Option value="add-profile">
                <Button color="primary" variant="outlined" icon={<PlusIcon />}>
                  Create Workspace
                </Button>
              </Select.Option>
            </Select>
          </div>

          {/* Navigation Items */}
          <div className="flex-1 overflow-y-auto">
            <DrawerNav onLinkClick={handleLinkClick} />
          </div>

          {/* Fixed Profile Section at Bottom - Mobile Only */}
          {getUserData && (
            <div className='px-3 pb-3 flex-shrink-0'>
              <Dropdown
                    menu={{
                      items: [
                        {
                          label: (
                            <Button
                              icon={<IntegrationIcon className="w-4 h-4 text-[#535862]" />}
                              type="link"
                              size="small"
                              onClick={() => {
                                navigate('/integrations');
                                if (sidebarContext) sidebarContext.closeMobileMenu();
                              }}
                              className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                            >
                              <span>Integrations</span>
                            </Button>
                          ),
                          key: 'integrations',
                        },
                        {
                          label: (
                            <Button
                              icon={<SettingsIcon className="w-4 h-4 text-[#535862]" />}
                              type="link"
                              size="small"
                              onClick={() => {
                                navigate('/company-settings/general');
                                if (sidebarContext) sidebarContext.closeMobileMenu();
                              }}
                              className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                            >
                              <span>Settings</span>
                            </Button>
                          ),
                          key: 'settings',
                        },
                        {
                          label: (
                            <Button
                              icon={<HelpIcon className="w-4 h-4 text-[#535862]" />}
                              type="link"
                              size="small"
                              onClick={() => {
                                navigate('/support/report-issue');
                                if (sidebarContext) sidebarContext.closeMobileMenu();
                              }}
                              className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                            >
                              <span>Report an Issue</span>
                            </Button>
                          ),
                          key: 'support',
                        },
                        ...(oogwaiUser && customersEmail?.emails && customersEmail.emails.length > 0 ? [
                          {
                            type: 'divider' as const,
                          },
                          {
                            label: (
                              <div className="" onClick={(e) => e.stopPropagation()}>
                                <p className="text-xs font-medium text-gray-500 mb-2">Impersonate account</p>
                                <Select
                                  showSearch
                                  placeholder="Search for a user..."
                                  value={shadowUser || undefined}
                                  onChange={async (email) => {
                                    setShadowUser(email);
                                    localStorage.setItem('oogw.ai_shadow_user_email', email);
                                    sessionStorage.removeItem('referral_code');
                                    await new Promise(resolve => setTimeout(resolve, 50));
                                    dispatch(baseApi.util.resetApiState());
                                    if (location.pathname.startsWith('/conversation')) {
                                      dispatch(updateNewConversation(true));
                                      try { localStorage.removeItem('latest_chat_id'); } catch (e) { /* ignore */ }
                                      navigate('/conversation', { replace: true });
                                    }
                                    if (sidebarContext) sidebarContext.closeMobileMenu();
                                  }}
                                  filterOption={(input, option) =>
                                    String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                  }
                                  className="w-full"
                                  bordered={false}
                                  placement="topLeft"
                                  dropdownMatchSelectWidth={true}
                                  dropdownStyle={{ zIndex: 99999 }}
                                  getPopupContainer={() => document.body}
                                  options={customersEmail.emails.map((email: string) => ({
                                    value: email,
                                    label: email,
                                  }))}
                                />
                              </div>
                            ),
                            key: 'impersonate',
                          }
                        ] : []),
                        {
                          type: 'divider' as const,
                        },
                        {
                          label: (
                            <Button
                              type="text"
                              size="small"
                              onClick={() => {
                                handleLogout();
                                if (sidebarContext) sidebarContext.closeMobileMenu();
                              }}
                              icon={<LogoutIcon className="w-4 h-4 text-[#535862]" />}
                              className="ant-dropdown-menu-button text-gray-900 flex items-center justify-start space-x-2 w-full"
                            >
                              <span>Sign out</span>
                            </Button>
                          ),
                          className: 'bg-gray-50',
                          key: 'signout',
                        }
                      ]
                    }}
                    trigger={['click']}
                    placement="topRight"
                    overlayClassName="user-menu-dropdown"
                    overlayStyle={{ zIndex: 9999 }}
                  >
                <div className={`border border-gray-300 rounded-2xl bg-white z-40 transition-transform duration-300 relative cursor-pointer hover:bg-gray-50 ${sidebarContext?.isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                  <div className="w-full flex items-center gap-3 p-4">
                    <div className="relative">
                      {getUserData?.profile_picture ? (
                        <img
                          src={getUserData.profile_picture}
                          alt={getUserData?.first_name || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <UserIcon className="w-10 h-10 p-2 text-gray-600 rounded-full border border-gray-300 bg-white" />
                      )}
                      <div className="absolute bottom-0 right-0 w-2.5 h-2.5 border-2 border-white rounded-full bg-green-500" />
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {getUserData?.first_name || getUserData?.last_name
                          ? `${getUserData?.first_name || ""} ${getUserData?.last_name || ""}`.trim()
                          : getUserData?.email?.split('@')[0] || 'User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {getUserData?.email || ''}
                      </p>
                    </div>
                    <ChevronSelectorVertical className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              </Dropdown>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div className='col-span-1 md:col-span-9 xl:col-span-10 px-4 md:px-0 md:pr-4'>
          <Outlet />
        </div>
      </div>
    </div>
  )
}

export default CompanySettings
